/**
 * 팝업 창(모달)을 **창처럼** — 끌어서 이동 · 앱 안에서 최대화 · 새 OS 창으로 분리 · 분리한 창에서 전체 화면(2026-10-06 사용자 요구:
 * 「모니터링·오라클 전문가 하위 창은 모두 이동·전체화면 — 듀얼 모니터면 다른 모니터에서 전체 화면으로」).
 *
 * 브라우저 안의 레이어는 화면 밖으로 못 나간다. 그래서 「새 창으로」 는 별도 OS 창(utils/popoutWindow)을 열어 같은 React 트리를 그린다.
 * **화면은 늘 같은 포털 그릇(holder) 하나에 그리고, 분리·되돌리기는 그 그릇의 DOM 노드만 옮긴다**(2026-10-16) — 예전에는 앱 안에서는
 * 제자리에, 분리하면 포털로 그려 React 가 다른 구조로 보고 창 안을 통째로 새로 만들었다(고른 탭·입력·조회 결과가 처음으로 돌아갔다 — 신고).
 * 그릇은 앱 안에서는 그 자리(display:contents 앵커 안)에 있어 배치·CSS 는 예전과 같다. 그 창을 다른 모니터로 끌어 놓고 ⤢ 를 누르면 그 모니터의 전체 화면이 된다.
 * 분리한 창을 닫으면 **앱 안으로 돌아온다**(작업 중인 내용이 사라지지 않게 — 창 자체를 닫으려면 창 안의 닫기).
 *
 * 쓰는 법(AppModal 은 `windowed` 로 켠다. 자체 틀을 쓰는 창은 직접):
 *   const win = useModalWindow({ title: '오라클 성능 분석' });
 *   return win.wrap(
 *     <div className="xxx-overlay" style={win.overlayStyle}>
 *       <div className="app-modal xxx" ref={win.frameRef} style={win.frameStyle}>
 *         <div className="app-modal-header" {...win.headerProps}> …제목… {win.controls} …닫기… </div>
 *
 * 이미 자체 최대화(isFullscreen)가 있는 창은 `maximized`·`onToggleMaximize` 를 넘기면 그 상태를 쓰고 ⤢ 단추는 그 창 것을 둔다.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';  // eslint-disable-line no-unused-vars -- JSX
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiMaximize2, FiMinimize2, FiExternalLink, FiCornerDownLeft } from 'react-icons/fi';
import { openPopout } from '../../utils/popoutWindow';
import { useWorkspaceDockApi } from '../workspace/WorkspaceDock';

import { toCssPx } from '../../utils/appZoom';
/** 끌기를 시작하지 않는 곳 — 제목줄 안의 단추·입력 */
const INTERACTIVE = 'button, input, select, textarea, a, label, [role="button"], [contenteditable="true"]';

const FILL = {
    width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', minWidth: 0,
    margin: 0, borderRadius: 0, transform: 'none', boxSizing: 'border-box',
};
/** 작업 탭에서 칸의 절반 폭 — 팝업이던 좁은 화면(설정·연결 관리 등)이 탭 전체로 늘어나 가로가 과해지지 않게(2026-11-04 사용자 요청) */
const HALF = { ...FILL, width: '50%', minWidth: 'min(640px, 100%)', borderRight: '1px solid var(--color-border, #e5e7eb)' };

/**
 * noPopout — 「새 창으로 분리」 를 끈다(단추는 꺼진 채 자리만 남는다). half — 작업 탭에서 절반 폭.
 */
export default function useModalWindow({ title, maximized: maxProp, onToggleMaximize, enabled = true, popoutSize, dock: dockable = enabled, onClose, icon, withResult = false, inline = false, noPopout = false, half = false } = {}) {
    const { t } = useTranslation();
    // 작업 탭(components/workspace/WorkspaceDock, 2026-10-19) — 「창처럼」 창은 팝업이 아니라 편집기 자리의 탭으로 열린다.
    // 그릇(holder)을 그 탭의 칸으로 옮길 뿐이라 창의 상태·내용은 그대로다. 열려 있는지는 앵커가 붙었는지로 안다(창마다 open 을 넘기지 않게)
    // 칸·올리기만 읽는다 — 앞에 온 탭이 바뀔 때마다 창이 다시 그려지지 않게(useWorkspaceDockApi)
    const ws = useWorkspaceDockApi();
    const dockId = useId();
    const [mounted, setMounted] = useState(false);
    const docked = !!ws && dockable && mounted;
    // 제자리에 놓인 화면 — 작업 탭에 열렸거나(docked), 편집기 칸 안에 그려진 화면(inline — DetachableView).
    // 움직이거나 앱 안에서 최대화할 것이 없고, 「전체 화면」 은 새 창으로 분리한 뒤에만 켜진다(2026-10-19 사용자 요청)
    const placed = docked || inline;
    const slotEl = docked ? ws.slots[dockId] : null;
    const closeRef = useRef(onClose);
    closeRef.current = onClose;
    const controlled = typeof maxProp === 'boolean';
    const [maxSelf, setMaxSelf] = useState(false);
    const maximized = controlled ? maxProp : maxSelf;
    const toggleMax = useCallback(() => {
        if (controlled) { if (onToggleMaximize) onToggleMaximize(); } else setMaxSelf(v => !v);
    }, [controlled, onToggleMaximize]);

    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [pop, setPop] = useState(null);            // { popup, container, close } — 분리된 동안
    const [popFull, setPopFull] = useState(false);   // 분리한 창의 전체 화면
    const frameRef = useRef(null);
    const drag = useRef(null);
    // 포털 그릇 — 앱 안이든 분리 창이든 늘 여기에 그린다(옮기는 것은 이 노드뿐). 앵커가 붙기 전에는 body 에 둔다(첫 그리기의 측정이 0 이 되지 않게)
    const holderRef = useRef(null);
    if (!holderRef.current && typeof document !== 'undefined') {
        const h = document.createElement('div');
        h.className = 'modal-win-holder';
        h.style.display = 'contents';
        document.body.appendChild(h);
        holderRef.current = h;
    }
    const anchorEl = useRef(null);
    const popRef = useRef(null);
    const dockSlotRef = useRef(null);   // 작업 탭의 칸 — 있으면 그곳이 「앱 안의 자리」 다
    dockSlotRef.current = slotEl;
    const dockWaitRef = useRef(false);  // 작업 탭으로 갈 창 — 칸이 생기기 전에는 앵커(오버레이 자리)에 두지 않는다
    dockWaitRef.current = !!ws && dockable;
    /** 그릇을 앱 안의 자리로 — 분리 창을 닫기 **전에** 부른다(닫히는 문서에 화면을 남겨 두지 않게) */
    const moveHome = useCallback(() => {
        const h = holderRef.current;
        const home = dockSlotRef.current || (dockWaitRef.current ? null : anchorEl.current) || document.body;
        if (h && h.parentNode !== home) home.appendChild(h);
    }, []);

    // ── 이동 ── 제목줄을 끌어 옮긴다(최대화·분리 중에는 움직일 것이 없다)
    const onPointerDown = useCallback((e) => {
        if (!enabled || maximized || pop || placed || e.button !== 0 || e.target.closest(INTERACTIVE)) return;
        const rect = frameRef.current ? frameRef.current.getBoundingClientRect() : null;
        drag.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y, rect };
        e.currentTarget.setPointerCapture(e.pointerId);
        e.preventDefault();
    }, [enabled, maximized, pop, placed, offset]);
    const onPointerMove = useCallback((e) => {
        const d = drag.current;
        if (!d) return;
        let dx = e.clientX - d.sx;
        let dy = e.clientY - d.sy;
        // 제목줄이 화면 밖으로 사라지지 않게 — 위로는 0 까지, 나머지는 창이 80px 은 보이게
        if (d.rect) {
            const view = e.view || window;
            const vw = view.innerWidth; const vh = view.innerHeight;
            dy = Math.max(-d.rect.top, Math.min(dy, vh - d.rect.top - 40));
            dx = Math.max(80 - d.rect.right, Math.min(dx, vw - d.rect.left - 80));
        }
        // 위 계산은 화면 px(clientX·rect·innerWidth 가 같은 단위) — 옮기는 값만 CSS px 로(웹의 CSS zoom, utils/appZoom)
        setOffset({ x: d.ox + toCssPx(dx), y: d.oy + toCssPx(dy) });
    }, []);
    const onPointerUp = useCallback((e) => {
        if (!drag.current) return;
        drag.current = null;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_e) { /* 이미 풀림 */ }
    }, []);
    const onDoubleClick = useCallback((e) => {
        if (!enabled || pop || placed || e.target.closest(INTERACTIVE)) return;
        toggleMax();
    }, [enabled, pop, placed, toggleMax]);

    // ── 새 창으로 / 되돌리기 ──
    const popOut = useCallback(() => {
        const rect = frameRef.current ? frameRef.current.getBoundingClientRect() : null;
        const size = popoutSize || (rect ? { width: Math.round(rect.width), height: Math.round(rect.height) } : {});
        const p = openPopout({
            title: typeof title === 'string' ? title : 'LiveSQL',
            width: Math.max(640, size.width || 1200), height: Math.max(480, size.height || 800),
            // 사용자가 분리한 창을 닫으면 앱 안으로 돌아온다(내용을 잃지 않게)
            onClosed: () => { moveHome(); setPop(null); setPopFull(false); },
        });
        if (!p) return;   // 팝업 차단 — 그대로 앱 안에 둔다
        setPop(p);
    }, [title, popoutSize, moveHome]);
    const dockBack = useCallback(() => {
        moveHome();
        setPop((p) => { if (p) p.close(); return null; });
        setPopFull(false);
    }, [moveHome]);
    const dockBackRef = useRef(dockBack);
    dockBackRef.current = dockBack;
    // 창(컴포넌트)이 닫히면 분리한 창도 닫는다
    useEffect(() => () => { if (pop) pop.close(); }, [pop]);
    // 그릇을 지금 있어야 할 곳으로 — 분리 중이면 그 창, 아니면 앱 안의 앵커. DOM 노드만 옮기므로 React 상태는 그대로다
    popRef.current = pop;
    // 작업 탭으로 갈 창은 칸이 생길 때까지 숨겨 둔다 — 그 사이 오버레이로 한 장면 번쩍이지 않게
    const waitingDock = !!ws && dockable;
    useLayoutEffect(() => {
        const h = holderRef.current;
        if (!h) return;
        const target = pop ? pop.container : (waitingDock ? slotEl : anchorEl.current);
        h.style.display = target || !waitingDock ? 'contents' : 'none';
        if (target && h.parentNode !== target) target.appendChild(h);
    }, [pop, waitingDock, slotEl, mounted]);
    useEffect(() => () => { const h = holderRef.current; if (h && h.parentNode) h.parentNode.removeChild(h); }, []);
    const anchorRef = useCallback((el) => {
        anchorEl.current = el;
        setMounted(!!el);
        const h = holderRef.current;
        if (el && h && !popRef.current && !dockWaitRef.current && h.parentNode !== el) el.appendChild(h);
    }, []);

    // 열려 있는 동안 작업 탭으로 올린다(닫히면 내린다). 탭의 ✕ 는 창의 닫기와 같다 — onClose 가 없으면 창의 닫기 단추를 누른다
    const popped = !!pop;
    const wsRegister = ws ? ws.register : null;
    const iconRef = useRef(icon);
    iconRef.current = icon;
    useLayoutEffect(() => {
        if (!docked || !wsRegister) return undefined;
        // 아이콘은 렌더마다 새 JSX 라 비교하지 않는다(처음 것을 쓴다) — 비교하면 올리기 ↔ 다시 그리기가 끝없이 돈다
        wsRegister(dockId, { title: typeof title === 'string' ? title : '', icon: iconRef.current || null, popped, withResult: !!withResult }, {
            close: () => {
                if (closeRef.current) { closeRef.current(); return; }
                const btn = frameRef.current && frameRef.current.querySelector('.app-modal-close');
                if (btn) btn.click();
            },
            dockBack: () => dockBackRef.current && dockBackRef.current(),
        });
        return undefined;
    }, [docked, wsRegister, dockId, title, popped, withResult]);
    useEffect(() => {
        if (!docked) return undefined;
        const unregister = ws.unregister;
        return () => unregister(dockId);
    }, [docked, dockId]);   // eslint-disable-line react-hooks/exhaustive-deps -- 닫힐 때 한 번만 내린다(ws 가 바뀔 때마다 내렸다 올리지 않게)

    // 분리한 창의 전체 화면 — 그 창이 있는 모니터에서
    useEffect(() => {
        if (!pop) return undefined;
        const doc = pop.popup.document;
        const onChange = () => setPopFull(!!doc.fullscreenElement);
        doc.addEventListener('fullscreenchange', onChange);
        return () => doc.removeEventListener('fullscreenchange', onChange);
    }, [pop]);
    const togglePopFull = useCallback(() => {
        if (!pop) return;
        const doc = pop.popup.document;
        if (doc.fullscreenElement) doc.exitFullscreen().catch(() => {});
        else doc.documentElement.requestFullscreen().catch(() => {});
    }, [pop]);

    const filled = !!pop || maximized || placed;
    const overlayStyle = pop
        ? { position: 'fixed', inset: 0, background: 'transparent', padding: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'stretch' }
        // 작업 탭의 칸을 채운다 — 어둡게 덮지도, 다른 화면 위로 뜨지도 않는다
        : docked ? { position: 'absolute', inset: 0, background: 'transparent', padding: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'stretch', zIndex: 'auto', animation: 'none' }
        : (maximized ? { padding: 0 } : undefined);
    const frameStyle = filled ? (half && placed && !pop ? HALF : FILL) : (offset.x || offset.y ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined);

    const btn = (onClick, label, icon, disabled = false) => (
        <button type="button" className="app-modal-iconbtn modal-win-btn" onClick={onClick} title={label} aria-label={label} disabled={disabled}>{icon}</button>
    );
    const controls = !enabled ? null : (
        <span className="modal-win-controls">
            {pop ? (
                <>
                    {btn(togglePopFull, t(popFull ? 'common:win.exitFullscreen' : 'common:win.fullscreen'),
                        popFull ? <FiMinimize2 size={14} /> : <FiMaximize2 size={14} />)}
                    {btn(dockBack, t('common:win.dockBack'), <FiCornerDownLeft size={14} />)}
                </>
            ) : placed ? (
                <>
                    {/* 제자리에서는 전체 화면이 없다 — 새 창으로 분리한 뒤 그 창(다른 모니터)에서 켠다. 자리를 알려 주려고 꺼진 채 둔다 */}
                    {btn(undefined, t('common:win.fullscreenAfterPopOut'), <FiMaximize2 size={14} />, true)}
                    {btn(noPopout ? undefined : popOut, t(noPopout ? 'common:win.popOutOff' : 'common:win.popOut'), <FiExternalLink size={14} />, noPopout)}
                </>
            ) : (
                <>
                    {!controlled ? btn(toggleMax, t(maximized ? 'common:win.restore' : 'common:win.maximize'),
                        maximized ? <FiMinimize2 size={14} /> : <FiMaximize2 size={14} />) : null}
                    {btn(noPopout ? undefined : popOut, t(noPopout ? 'common:win.popOutOff' : 'common:win.popOut'), <FiExternalLink size={14} />, noPopout)}
                </>
            )}
        </span>
    );

    // data-win-drag — 끌 수 있을 때만 커서가 이동 모양(styles/appModal.css)
    const headerProps = enabled ? {
        onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onDoubleClick,
        'data-win-drag': !filled ? 'true' : undefined,
    } : {};

    /** 늘 같은 모양 — 앵커(앱 안의 자리) + 그릇으로의 포털. 분리 여부에 따라 구조가 바뀌면 React 가 창 안을 새로 만든다 */
    const wrap = useCallback((node) => (holderRef.current ? (
        <>
            <span ref={anchorRef} className="modal-win-anchor" style={{ display: 'contents' }} />
            {createPortal(node, holderRef.current)}
        </>
    ) : node), [anchorRef]);

    /** 창을 닫을 때 — 다음에 열면 제자리·기본 크기·앱 안에서 시작한다 */
    const reset = useCallback(() => {
        setOffset({ x: 0, y: 0 });
        if (!controlled) setMaxSelf(false);
        dockBack();
    }, [controlled, dockBack]);

    return { frameRef, frameStyle, overlayStyle, headerProps, controls, wrap, reset, popped: !!pop, maximized, popFullscreen: popFull, docked, placed };
}
